// Angular
import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Data } from '@angular/router';

// RxJS
import {
  EMPTY,
  firstValueFrom,
  forkJoin,
  from,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

// Services
import { JudgeReviewApiService } from '@app/services/api/judge-review/judge-review-api.service';
import {
  JsonPatchOperation,
  PatchHelpersService,
} from '@app/services/utilities/patch-helpers.service';

// Data Contracts
import {
  CaseParticipant,
  JudgeDocumentOption,
  JudgeReviewActionDocument,
  JudgeReviewAnnotation,
  JudgeReviewFiling,
  JudgeReviewFilingAdditionalRecipient,
  JudgeReviewFilingConfiguration,
  JudgeReviewFilingMode,
  JudgeReviewMailingAddress,
  JudgeReviewSubmitResponse,
} from '@data-contracts';

// Local
import { isEqual } from 'lodash';
import { JudgeAnnotation } from '@feature/jr/shared/models/annotation.model';
import {
  resolveCreatesNewFiling,
  resolveJudicialActionName,
} from './judge-review-config.utils';
import { JudgeReviewUploadTarget } from '../models/judge-review-studio.view-model';
import {
  ACTION_DISTRIBUTION_PATH,
  ACTION_PATH,
  actionDocumentPath,
  actionDocuments,
  isSubmittedStatus,
} from './judge-review-action.utils';
import { QueuedJudgeReviewDocument } from './judge-review-document-queue.service';
import { JudgeReviewOutgoingChoiceService } from './judge-review-outgoing-choice.service';
import {
  mapAnnotationsToDto,
  mapPersistedAnnotations,
} from './map-judge-review-annotations.utils';
import { mapUploadTarget } from './map-judge-review-studio.utils';

/**
 * Feature state for Judge Review: the filing, configuration and submit-response
 * DTOs, and the commands that load, patch, submit and reset them. The raw
 * `@data-contracts` DTOs are held in **private** signals (they back the PATCH /
 * submit / proceed builders). The UI models components bind are mapped by the
 * per-page `JudgeReview*ViewModelService`s from the read-only DTO signals below;
 * components never bind DTO shapes and never import `@data-contracts`.
 */
@Injectable({
  providedIn: 'root',
})
export class JudgeReviewDataService {
  private readonly apiService = inject(JudgeReviewApiService);
  private readonly outgoingChoice = inject(JudgeReviewOutgoingChoiceService);
  private readonly patchHelpersService = inject(PatchHelpersService);

  private _filing = signal<JudgeReviewFiling | undefined>(undefined);
  private _originalFiling = signal<JudgeReviewFiling | undefined>(undefined);
  private _config = signal<JudgeReviewFilingConfiguration | undefined>(
    undefined,
  );
  private _submitResponse = signal<JudgeReviewSubmitResponse | undefined>(
    undefined,
  );

  /**
   * Read-only DTO state for the view-model services in this folder
   * (`JudgeReview*ViewModelService`), which map it to UI models. Components never
   * read these; they bind the view-model services' output.
   */
  readonly filingDto = this._filing.asReadonly();
  readonly configDto = this._config.asReadonly();
  readonly submitResponseDto = this._submitResponse.asReadonly();

  readonly isOpf = computed(() => this._filing()?.isOpf ?? false);

  /*********************************************************************************************************************
   * Enum constants — surfaced so studio / detail commands can set form and DTO
   * enum values without importing `@data-contracts` into the components.
   ********************************************************************************************************************/

  /** {@link JudgeDocumentOption} values (KeepOriginal / Replace / Attach). */
  readonly documentOptions = JudgeDocumentOption;

  /*********************************************************************************************************************
   * Shared scalars (filing id, lifecycle flags, navigation ids).
   ********************************************************************************************************************/

  /** The reviewed filing unique id (GUID) used for navigation / PATCH / submit. */
  readonly filingId = computed(
    () => this._filing()?.reviewedFilingUniqueId ?? null,
  );

  /** Whether a judge review submission for this filing is already in progress. */
  readonly inProcess = computed(() => this._filing()?.inProcess ?? false);

  /** True once a submit response exists (drives the receipt route guard). */
  readonly hasSubmitResponse = computed(() => this._submitResponse() != null);

  /** Court id for the lead case navigation (Transaction Detail "open case"). */
  readonly courtId = computed(() => this._filing()?.courtId ?? null);

  /** Lead case id for the case navigation. */
  readonly leadCaseId = computed(
    () => this._filing()?.judgeReviewCases?.[0]?.caseId ?? null,
  );

  /**
   * Court / case-class setting that lets the judge opt out of File & Serve and
   * only update the judge-review status (no new filing, nothing served).
   */
  readonly allowStatusUpdateOnly = computed(
    () => this._config()?.allowStatusUpdateOnly ?? false,
  );

  /*********************************************************************************************************************
   * Multi-document review state.
   *
   * A judge-review filing can carry several documents. Each is marked reviewed
   * server-side (see `markDocumentReviewed`) once a non-Pending judicial action is
   * selected AND its Continue completed without errors, so the flag below is API
   * truth rather than a local guess. The studio uses it to decide whether Continue
   * routes back to transaction-detail or on to confirmation; transaction-detail uses
   * it to show progress and to offer the next un-reviewed document.
   ********************************************************************************************************************/

  /** Integer ids of the documents in the in-progress action, in action order. */
  readonly documentIds = computed<number[]>(() =>
    actionDocuments(this._filing())
      .map(document => document.existingDocumentId)
      .filter((id): id is number => id != null),
  );

  /** True when the in-progress action carries more than one document. */
  readonly isMultiDocument = computed(() => this.documentIds().length > 1);

  /** Integer ids of the action's documents the judge has not finished reviewing, in action order. */
  readonly unreviewedDocumentIds = computed<number[]>(() =>
    actionDocuments(this._filing())
      .filter(document => !document.isReviewed)
      .map(document => document.existingDocumentId)
      .filter((id): id is number => id != null),
  );

  /**
   * True once every document in the in-progress action is reviewed. An empty action
   * counts as complete so an unexpected shape can never strand the judge on the
   * transaction-detail page with nothing left to open.
   */
  readonly allDocumentsReviewed = computed(
    () => this.unreviewedDocumentIds().length === 0,
  );

  /**
   * True when the filing has documents and every one keeps its original
   * (no Replace / Attach). Mirrors the API's status-update-only guard, which
   * ignores `statusUpdateOnly` unless this holds.
   */
  readonly allDocumentsKeepOriginal = computed(() => {
    const documents = actionDocuments(this._filing());
    return (
      documents.length > 0 &&
      documents.every(
        document =>
          document.judgeDocumentOption === JudgeDocumentOption.KeepOriginal,
      )
    );
  });

  /**
   * Whether the judge may choose "update without serving" — the court/case class
   * allows it and every document is KeepOriginal. When false the option is not
   * offered, so a flag the server would ignore is never presented as a choice.
   */
  readonly canUpdateStatusOnly = computed(
    () => this.allowStatusUpdateOnly() && this.allDocumentsKeepOriginal(),
  );

  /**
   * The judge's persisted "update without serving" choice. Read from the filing
   * (server truth) so a SignalR-driven refresh keeps the toggle in sync.
   * Defaults to false — File & Serve.
   */
  readonly statusUpdateOnly = computed(
    () => this._filing()?.currentAction?.statusUpdateOnly === true,
  );

  /** Whether the judge has completed their review of a specific document. */
  isDocumentReviewed(existingDocumentId: number | null): boolean {
    if (existingDocumentId == null) {
      return false;
    }
    const filing = this._filing();
    const document =
      actionDocuments(filing).find(
        candidate => candidate.existingDocumentId === existingDocumentId,
      ) ??
      filing?.judgeReviewDocuments?.find(
        candidate => candidate.existingDocumentId === existingDocumentId,
      );
    return document?.isReviewed === true;
  }

  /**
   * The next document the judge should open, skipping `excludeDocumentId` (the one
   * they just finished — its `isReviewed` may not have round-tripped yet).
   *
   * @param excludeDocumentId Document to skip, typically the one just reviewed.
   * @returns The next un-reviewed document id, or `null` when none remain.
   */
  nextUnreviewedDocumentId(
    excludeDocumentId: number | null = null,
  ): number | null {
    return (
      this.unreviewedDocumentIds().find(id => id !== excludeDocumentId) ?? null
    );
  }

  /**
   * Records the judge's completed review of a document at the end of a successful
   * Continue. The API re-checks the non-Pending judicial action before setting the
   * flag, so a 400 here means the document does not actually qualify as reviewed.
   *
   * The response is the updated filing; it is written straight into the local state
   * (and the diff baseline) so the Continue routing decision that follows reads the
   * server's view rather than a stale one.
   *
   * @param existingDocumentId The integer document id just reviewed.
   * @returns Observable of the updated filing, or `EMPTY` when no filing is loaded.
   */
  markDocumentReviewed(
    existingDocumentId: number,
  ): Observable<JudgeReviewFiling> {
    const filingId = this._filing()?.reviewedFilingUniqueId;
    if (!filingId) {
      return EMPTY;
    }
    return this.apiService
      .markDocumentReviewed(filingId, existingDocumentId)
      .pipe(tap(filing => this.setJudgeReviewFiling(filing)));
  }

  /*********************************************************************************************************************
   * Per-document resolvers (depend on the active document, so they take the id
   * instead of being standalone signals).
   ********************************************************************************************************************/

  /** Display name of a document's selected judicial action, or null. */
  judicialActionName(
    documentId: number | null,
    judicialActionId: string | null | undefined,
  ): string | null {
    return resolveJudicialActionName(
      this._config(),
      documentId,
      judicialActionId,
    );
  }

  /**
   * Whether a document's selected judicial action creates a new filing
   * (grant-type → Review & Sign studio) vs a record-only disposition. Defaults
   * to true (studio) when the flag is unknown, mirroring the previous inline
   * `createNewFiling !== false` check.
   */
  createsNewFiling(
    documentId: number | null,
    judicialActionId: string | null | undefined,
  ): boolean {
    return resolveCreatesNewFiling(
      this._config(),
      documentId,
      judicialActionId,
    );
  }

  /** Seed data for the Transaction Detail per-document action map. */
  documentActionSeeds(): {
    documentId: number;
    selectedJudicialActionId: string | null;
  }[] {
    const filing = this._filing();
    const inAction = new Map(
      actionDocuments(filing).map(
        document => [document.existingDocumentId, document] as const,
      ),
    );
    return (filing?.judgeReviewDocuments ?? [])
      .filter(
        document =>
          document.existingDocumentId != null &&
          !isSubmittedStatus(document.status),
      )
      .map(document => ({
        documentId: document.existingDocumentId as number,
        selectedJudicialActionId:
          inAction.get(document.existingDocumentId)?.selectedJudicialActionId ??
          document.selectedJudicialActionId ??
          null,
      }));
  }

  /*********************************************************************************************************************
   * State hydration / mutation
   ********************************************************************************************************************/

  /**
   * Hydrates the feature state from the route's resolved data. Keeps the
   * DTO-typed cast off the component so it never names `@data-contracts` types.
   */
  hydrateFromRoute(data: Data): void {
    const config = data['judgeReviewConfiguration'] as
      | JudgeReviewFilingConfiguration
      | undefined;
    if (config) {
      this.setJudgeReviewConfiguration(config);
    }
    const filing = data['judgeReviewData'] as JudgeReviewFiling | undefined;
    if (filing) {
      this.setJudgeReviewFiling(filing);
    }
  }

  setJudgeReviewFiling(filing: JudgeReviewFiling): void {
    this.outgoingChoice.bind(filing.reviewedFilingUniqueId ?? null);
    this._filing.set(filing);
    this._originalFiling.set(structuredClone(filing));
  }

  /**
   * Re-fetches the filing from the API using the current `reviewedFilingUniqueId`
   * and updates the local signal state. Returns `EMPTY` when no filing id is
   * available. Used by the SignalR `DocumentReady` handler to resync after a push
   * or after an automatic reconnect.
   *
   * @returns Observable of the refreshed {@link JudgeReviewFiling}, or `EMPTY`.
   */
  refreshFiling(): Observable<JudgeReviewFiling> {
    const id = this._filing()?.reviewedFilingUniqueId;
    if (!id) {
      return EMPTY;
    }
    return this.apiService
      .getJudgeReviewFiling(id)
      .pipe(tap(filing => this.setJudgeReviewFiling(filing)));
  }

  setJudgeReviewConfiguration(
    judgeReviewConfiguration: JudgeReviewFilingConfiguration,
  ): void {
    this._config.set(judgeReviewConfiguration);
  }

  setJudgeReviewSubmitResponse(response: JudgeReviewSubmitResponse): void {
    this._submitResponse.set(response);
  }

  /**
   * The working-copy filing DTO. Builder-only: consumed solely by the studio's
   * reactive-form builder (the §4 PATCH/form-builder allowance). Components bind
   * UI models — they must not read DTO fields off this. Kept a DTO because the
   * form is shaped to the DTO for JSON-Patch diffing.
   */
  getJudgeReviewFiling(): JudgeReviewFiling | undefined {
    return this._filing();
  }

  /** Per-document action configuration (config DTO) — internal use only. */

  /** Maps the studio's recipient choice to the API distribution mode. */
  filingModeForRecipients(
    mode: 'all' | 'custom' | 'none',
  ): JudgeReviewFilingMode {
    switch (mode) {
      case 'none':
        return JudgeReviewFilingMode.FileOnly;
      case 'custom':
        return JudgeReviewFilingMode.CustomServiceList;
      case 'all':
      default:
        return JudgeReviewFilingMode.FileAndServe;
    }
  }

  /** Maps the action's saved distribution mode back to the studio's recipient choice. */
  savedRecipients(): 'all' | 'custom' | 'none' {
    switch (this._filing()?.currentAction?.distribution?.filingMode) {
      case JudgeReviewFilingMode.FileOnly:
        return 'none';
      case JudgeReviewFilingMode.CustomServiceList:
        return 'custom';
      default:
        return 'all';
    }
  }

  /**
   * @param formValue - The current (edited) filing value from the form.
   * @returns The server's patched filing, or the current filing when nothing changed.
   */
  saveJudgeReviewFiling(
    formValue: JudgeReviewFiling,
  ): Promise<JudgeReviewFiling> {
    const original = this._originalFiling();
    const filingId = original?.reviewedFilingUniqueId;
    if (!original || !filingId) {
      return Promise.reject(new Error('No filing data available to patch.'));
    }

    const originalAction = original.currentAction;
    if (!originalAction) {
      return Promise.reject(
        new Error(
          'No action in progress to patch. Start one with Review & Sign.',
        ),
      );
    }

    // The recipient collections are owned by the recipients and address pages,
    // not the studio form, and belong to a custom service list. Kept while the
    // distribution stays custom (so the diff doesn't read them as removed), and
    // cleared with the participant ids when the judge picks another mode. An
    // empty collection is left as-is, so an ordinary save sends no op for it.
    const preserved = this.withPreservedAnnotations(formValue, original);
    const formAction = preserved.currentAction;
    const isCustom =
      formAction?.distribution?.filingMode ===
      JudgeReviewFilingMode.CustomServiceList;
    const keepOrClear = <T>(
      saved: T[] | null | undefined,
    ): T[] | null | undefined => (isCustom || !saved?.length ? saved : null);
    // Everything the form does not own (the roster, server-owned fields) is carried
    // over from the baseline so the diff only contains the judge's edits, all under
    // `/currentAction`.
    const candidate: JudgeReviewFiling = {
      ...original,
      judgeReviewerId: preserved.judgeReviewerId ?? original.judgeReviewerId,
      currentAction: {
        ...originalAction,
        statusUpdateOnly:
          formAction?.statusUpdateOnly ?? originalAction.statusUpdateOnly,
        distribution: {
          ...originalAction.distribution,
          ...this.omitUnsetNulls(
            formAction?.distribution,
            originalAction.distribution,
          ),
          additionalRecipients: keepOrClear(
            originalAction.distribution?.additionalRecipients,
          ),
          mailingAddresses: keepOrClear(
            originalAction.distribution?.mailingAddresses,
          ),
        },
        documents: this.mergeWithServerDocuments(
          formAction?.documents,
          originalAction.documents,
        ),
      },
    };

    // The edits are diffed against the action, not the whole filing: the client's
    // readonly check treats `JudgeReviewFiling.currentAction` as a read-only subtree and
    // would drop every `/currentAction/...` op, while the API only blocks the object
    // itself and patches into it. `JudgeReviewAction` carries the real per-field
    // constraints (collectionId, document membership, ...). Paths are re-rooted below.
    const patchData: JsonPatchOperation[] = this.patchHelpersService
      .generatePatchOperations(
        original.currentAction,
        candidate.currentAction,
        'JudgeReviewAction',
      )
      .map(operation => ({
        ...operation,
        path: `${ACTION_PATH}${operation.path}`,
      }));
    // A clerk acting for a judge can change the reviewer.
    if (candidate.judgeReviewerId !== original.judgeReviewerId) {
      patchData.unshift({
        op: 'replace',
        path: '/judgeReviewerId',
        value: candidate.judgeReviewerId,
      });
    }

    if (patchData.length === 0) {
      return Promise.resolve(this._filing() ?? original);
    }

    return this.submitFilingPatch(filingId, patchData);
  }

  /**
   * Lays the form's document values over the server's copy of each action document.
   *
   * The form only maps the fields the judge edits. Diffed as-is, every other field the
   * server returned (for example the server-owned `resolvedOutcome`, which the client's
   * readonly check does not know about) would read as removed and be sent as a
   * `remove` op, which the API rejects as an invalid operation. Starting from the
   * server's document leaves those fields unchanged, so the diff holds only real edits.
   *
   * @param formDocuments The documents built from the form (may be absent).
   * @param serverDocuments The baseline action's documents.
   * @returns The form's documents completed with the server's fields, in form order.
   */
  private mergeWithServerDocuments(
    formDocuments: JudgeReviewActionDocument[] | undefined,
    serverDocuments: JudgeReviewActionDocument[] | undefined,
  ): JudgeReviewActionDocument[] | undefined {
    if (!formDocuments) {
      return serverDocuments;
    }
    const serverById = new Map(
      (serverDocuments ?? []).map(
        document => [document.existingDocumentId, document] as const,
      ),
    );
    return formDocuments.map(document => {
      const server = serverById.get(document.existingDocumentId);
      return { ...server, ...this.omitUnsetNulls(document, server) };
    });
  }

  /**
   * Drops the form's `null` values for fields the server did not return.
   *
   * An unset form control reads `null`, while the server leaves a field it has no value
   * for out of its JSON. Diffed as-is, that reads as an `add` of `null`, which the API
   * rejects for non-nullable fields (for example `courtRulingTransactionCharge`), failing
   * the whole patch as malformed. A field the server does return can still be cleared.
   */
  private omitUnsetNulls<T extends object>(
    value: T | undefined,
    baseline: T | undefined,
  ): Partial<T> | undefined {
    if (!value) {
      return value;
    }
    return Object.fromEntries(
      Object.entries(value).filter(
        ([key, fieldValue]) =>
          fieldValue !== null ||
          (baseline as Record<string, unknown> | undefined)?.[key] !==
            undefined,
      ),
    ) as Partial<T>;
  }

  /**
   * Re-attaches each document's persisted `annotations` to a form-built filing
   * before it is diffed.
   *
   * `mapFormToJudgeReviewFiling` maps the master form only, and annotations are
   * not form controls — they live in the studio's `annotations()` signal and are
   * persisted separately by {@link patchDocumentAnnotations}. Without this merge
   * the diff sees `annotations` present on the baseline and absent on the
   * candidate, and emits `remove /currentAction/documents/{n}/annotations` — which
   * silently discards the judge's placed signature server-side (Save Draft, the
   * upload slot reservation, the record-only continue) and nulls
   * `documentUploadUri` on the KeepOriginal finalize PATCH.
   *
   * A document that already carries an `annotations` key is left untouched, so a
   * caller that deliberately supplies a fresher set — the finalize path passes
   * the live signal value, which may be ahead of the last autosave — still wins.
   *
   * @param formValue The form-built filing about to be diffed.
   * @param baseline  The diff baseline (`_originalFiling`), i.e. server truth.
   * @returns `formValue` with baseline annotations filled in per document.
   */
  private withPreservedAnnotations(
    formValue: JudgeReviewFiling,
    baseline: JudgeReviewFiling,
  ): JudgeReviewFiling {
    const documents = formValue.currentAction?.documents;
    if (!documents?.length) {
      return formValue;
    }

    const baselineAnnotations = new Map(
      actionDocuments(baseline)
        .filter(doc => doc.annotations !== undefined)
        .map(doc => [doc.existingDocumentId, doc.annotations] as const),
    );
    if (baselineAnnotations.size === 0) {
      return formValue;
    }

    return {
      ...formValue,
      currentAction: {
        ...formValue.currentAction,
        documents: documents.map(document =>
          'annotations' in document ||
          !baselineAnnotations.has(document.existingDocumentId)
            ? document
            : {
                ...document,
                annotations: baselineAnnotations.get(
                  document.existingDocumentId,
                ),
              },
        ),
      },
    };
  }

  /**
   * PATCHes the three custom-recipient collections —
   * `customServiceListParticipantIds`, `additionalRecipients`, and
   * `mailingAddresses` — to the JudgeReviewFiling.
   *
   * Called from:
   * - The `/recipients` page when the judge clicks Continue, Back or Save as
   *   Draft (mailing addresses passed through as last saved, minus any for
   *   participants no longer selected).
   * - The `/address` page after the judge fills mailing addresses for
   *   unrepresented participants (selections passed through as last saved).
   *
   * Each changed collection is sent as a whole-array replace, not diffed.
   * The BE rejects element-level replace on primitive lists with a 500,
   * and positional diffs rewrite elements by index rather than identity.
   *
   * @param customServiceListParticipantIds `CaseParticipant.caseParticipantUniqueId` values selected on the recipients page.
   * @param additionalRecipients            Write-in additional recipients entered by the judge.
   * @param mailingAddresses                Mailing addresses for unrepresented recipients.
   */
  async saveRecipients(
    customServiceListParticipantIds: string[],
    additionalRecipients: JudgeReviewFilingAdditionalRecipient[],
    mailingAddresses: JudgeReviewMailingAddress[],
  ): Promise<JudgeReviewFiling> {
    const filingId = this._filing()?.reviewedFilingUniqueId;
    const original = this._originalFiling();
    if (!filingId || !original) {
      return Promise.reject(
        new Error('No filing available to patch recipients.'),
      );
    }

    const distribution = original.currentAction?.distribution;
    if (!original.currentAction) {
      return Promise.reject(
        new Error('No action in progress to patch recipients.'),
      );
    }

    const collections = [
      ['customServiceListParticipantIds', customServiceListParticipantIds],
      ['additionalRecipients', additionalRecipients],
      ['mailingAddresses', mailingAddresses],
    ] as const;

    const patchData: JsonPatchOperation[] = collections
      .filter(([key, value]) => !isEqual(distribution?.[key] ?? [], value))
      .map(([key, value]) => ({
        op: 'replace',
        path: `${ACTION_DISTRIBUTION_PATH}/${key}`,
        value,
      }));

    if (patchData.length === 0) {
      return Promise.resolve(this._filing() ?? original);
    }

    return this.submitFilingPatch(filingId, patchData);
  }

  /**
   * File & Serve upload path: PATCHes the filing (reserving the blob-upload
   * slot(s)) and returns the reserved slots for the given document as a UI
   * model. The container drives the blob PUT + commit off this result and never
   * touches the {@link JudgeReviewFiling} DTO. Returns null when the document is
   * absent from the patched filing.
   *
   * Validation errors on this PATCH are intentionally not surfaced: it only
   * reserves the upload slots — the PDFs aren't uploaded yet, so transient
   * readiness errors are expected and final validation happens at submit.
   *
   * @param formValue The current (edited) filing value from the form.
   * @param existingDocumentId The document whose upload slots are needed.
   */
  async saveFilingAndResolveUploadTarget(
    formValue: JudgeReviewFiling,
    existingDocumentId: number | null,
  ): Promise<JudgeReviewUploadTarget | null> {
    const patched = await this.saveJudgeReviewFiling(formValue);
    return mapUploadTarget(patched, existingDocumentId);
  }

  /**
   * Status-update-only path: PATCHes the filing and returns any validation-error
   * descriptions as a UI-model string list, keeping the {@link JudgeReviewFiling}
   * DTO at the boundary. An empty array means the PATCH is clean.
   *
   * @param formValue The current (edited) filing value from the form.
   */
  async saveFilingAndGetValidationErrors(
    formValue: JudgeReviewFiling,
  ): Promise<string[]> {
    const patched = await this.saveJudgeReviewFiling(formValue);
    return (patched.validationErrors ?? [])
      .map(error => error.description ?? '')
      .filter((description): description is string => !!description);
  }

  /**
   * Starts the action for the Transaction Detail "Review & Sign" command: sets the
   * action's documents to the queued ones, then records each queued document's
   * judicial action.
   *
   * Setting the documents runs the server's copy-on-open, which creates each document's
   * working copy (`judgeReviewDocumentStoreId`) — every studio call needs that id. It is
   * skipped when the action already holds exactly the queued documents, because
   * re-running it would discard the work already done on them. A record-only
   * disposition keeps its original document (status-update path).
   *
   * @param queued The documents the judge queued on the Transaction page.
   * @returns The server's patched filing.
   */
  proceedQueued(
    queued: readonly QueuedJudgeReviewDocument[],
  ): Observable<JudgeReviewFiling> {
    const filingId = this._filing()?.reviewedFilingUniqueId;
    if (!this._filing() || !filingId) {
      return throwError(() => new Error('No filing data available to patch.'));
    }

    const queuedIds = queued.map(item => item.documentId);
    const inAction = new Set(this.documentIds());
    const actionMatchesQueue =
      queuedIds.length === inAction.size &&
      queuedIds.every(id => inAction.has(id));

    const action$ = actionMatchesQueue
      ? of(this._filing() as JudgeReviewFiling)
      : this.apiService
          .putJudgeReviewCurrentActionDocuments(filingId, queuedIds)
          .pipe(tap(filing => this.setJudgeReviewFiling(filing)));

    return action$.pipe(switchMap(() => this.saveQueuedActions(queued)));
  }

  /** Records each queued document's judicial action on the in-progress action. */
  private saveQueuedActions(
    queued: readonly QueuedJudgeReviewDocument[],
  ): Observable<JudgeReviewFiling> {
    const current = this._filing();
    if (!current?.currentAction) {
      return throwError(
        () => new Error('The action did not start. Please try again.'),
      );
    }

    const byDocument = new Map(queued.map(item => [item.documentId, item]));
    const updated: JudgeReviewFiling = {
      ...current,
      currentAction: {
        ...current.currentAction,
        documents: (current.currentAction.documents ?? []).map(document => {
          const item =
            document.existingDocumentId != null
              ? byDocument.get(document.existingDocumentId)
              : undefined;
          if (!item) {
            return document;
          }
          return {
            ...document,
            selectedJudicialActionId: item.judicialActionId,
            judgeDocumentOption: item.createsNewFiling
              ? document.judgeDocumentOption
              : JudgeDocumentOption.KeepOriginal,
          };
        }),
      },
    };

    return from(this.saveJudgeReviewFiling(updated));
  }

  /**
   * @param filingId - The filing unique id (GUID) to patch.
   * @param patchData - The JSON Patch operations to apply.
   * @returns The server's patched filing.
   */
  async submitFilingPatch(
    filingId: string,
    patchData: JsonPatchOperation[],
  ): Promise<JudgeReviewFiling> {
    const result = await firstValueFrom(
      this.apiService.patchJudgeReviewFiling(filingId, patchData),
    );
    this.setJudgeReviewFiling(result);
    return result;
  }

  /**
   * Persists the judge's "update without serving" choice as a filing-level
   * JSON-Patch op and adopts the server's patched filing.
   *
   * @param statusUpdateOnly True to update the status only; false to File & Serve.
   * @returns Observable of the patched filing, or EMPTY when no filing is loaded.
   */
  setStatusUpdateOnly(
    statusUpdateOnly: boolean,
  ): Observable<JudgeReviewFiling> {
    const filingId = this._filing()?.reviewedFilingUniqueId;
    if (!filingId) {
      return EMPTY;
    }
    const ops: JsonPatchOperation[] = [
      {
        op: 'replace',
        path: `${ACTION_PATH}/statusUpdateOnly`,
        value: statusUpdateOnly,
      },
    ];
    return this.apiService
      .patchJudgeReviewFiling(filingId, ops)
      .pipe(tap(result => this.setJudgeReviewFiling(result)));
  }

  /**
   * Validates and submits the judge review filing. Thin passthrough to the API
   * service so the transport dependency stays behind the data service; callers
   * keep their own subscription / teardown.
   *
   * @param filingId The reviewedFilingUniqueId (GUID) of the filing to submit.
   * @returns The judge review submit response.
   */
  submitJudgeReviewFiling(
    filingId: string,
  ): Observable<JudgeReviewSubmitResponse> {
    return this.apiService.submitJudgeReviewFiling(filingId);
  }

  /**
   * Resets the working copy to its original state on the server, then opens the
   * action again on the same documents.
   *
   * The server's reset discards the in-progress action, so the documents the judge
   * was reviewing would drop out of the studio. Reopening starts over from the
   * filer's originals, not from nothing.
   *
   * @param filingId The filing unique id (GUID) to reset.
   * @param keepActions Judicial action ids to record again on the reopened documents,
   *   keyed by `existingDocumentId`. Saved before the filing is returned, so the
   *   server's validation never reports a missing disposition for them.
   * @returns The reopened filing.
   */
  resetFiling(
    filingId: string,
    keepActions: ReadonlyMap<number, string | null> = new Map(),
  ): Observable<JudgeReviewFiling> {
    const reopenIds = this.documentIds();
    return this.apiService.resetJudgeReviewFiling(filingId).pipe(
      // Adopt each server state as it arrives: once the reset lands the old action
      // and its working copies are gone, so a failed reopen must not leave the
      // client patching an action the server no longer has.
      tap(reset => this.setJudgeReviewFiling(reset)),
      switchMap(reset =>
        reopenIds.length
          ? this.apiService
              .putJudgeReviewCurrentActionDocuments(filingId, reopenIds)
              .pipe(
                tap(reopened => this.setJudgeReviewFiling(reopened)),
                switchMap(reopened =>
                  this.restoreActions(reopened, keepActions),
                ),
              )
          : of(reset),
      ),
    );
  }

  /**
   * Discards every edit made in this review session: deletes each judge rendering
   * (404 counts as success, the endpoint is idempotent), then resets the working
   * copy to the original on the server and reopens it ({@link resetFiling}). Shared
   * by the studio's Reset and by Cancel on every Judge Review page, so none of them
   * needs the studio's view model.
   *
   * @param filingId The filing unique id (GUID) to revert.
   * @param keepActions Judicial action ids to record again once the filing is
   *   reopened, keyed by `existingDocumentId` (see {@link resetFiling}).
   * @returns The reopened filing.
   */
  revertToOriginal(
    filingId: string,
    keepActions: ReadonlyMap<number, string | null> = new Map(),
  ): Observable<JudgeReviewFiling> {
    const renderingDocIds = actionDocuments(this._filing())
      .filter(
        document =>
          document.existingDocumentId != null &&
          !!document.judgeReviewDocumentStoreId,
      )
      .map(document => document.existingDocumentId as number);
    const deleteOps$ = renderingDocIds.length
      ? forkJoin(
          renderingDocIds.map(docId =>
            this.apiService
              .deleteJudgeReviewDocumentRendering(filingId, docId)
              .pipe(
                catchError(err =>
                  err instanceof HttpErrorResponse && err.status === 404
                    ? of<void>(undefined)
                    : throwError(() => err),
                ),
              ),
          ),
        )
      : of(null);
    return deleteOps$.pipe(
      switchMap(() => this.resetFiling(filingId, keepActions)),
    );
  }

  /** Records the kept judicial actions on a freshly reopened action. */
  private restoreActions(
    reopened: JudgeReviewFiling,
    keepActions: ReadonlyMap<number, string | null>,
  ): Observable<JudgeReviewFiling> {
    const hasActions = (reopened.currentAction?.documents ?? []).some(
      document =>
        document.existingDocumentId != null &&
        !!keepActions.get(document.existingDocumentId),
    );
    if (!reopened.currentAction || !hasActions) {
      return of(reopened);
    }
    // The save diffs against the baseline, so it must be the reopened filing.
    this.setJudgeReviewFiling(reopened);
    const updated: JudgeReviewFiling = {
      ...reopened,
      currentAction: {
        ...reopened.currentAction,
        documents: (reopened.currentAction.documents ?? []).map(document => {
          const actionId =
            document.existingDocumentId != null
              ? keepActions.get(document.existingDocumentId)
              : null;
          return actionId
            ? { ...document, selectedJudicialActionId: actionId }
            : document;
        }),
      },
    };
    return from(this.saveJudgeReviewFiling(updated));
  }

  /**
   * Persists annotations for a specific document via a targeted JSON Patch
   * replace on currentAction/documents/{idx}/annotations.
   * Updates both _filing and _originalFiling so this write becomes the new diff
   * baseline. Note that the baseline alone is not enough to keep subsequent form
   * PATCH diffs clean: form-built filings carry no `annotations` key at all, so
   * {@link saveJudgeReviewFiling} re-attaches the baseline values via
   * {@link withPreservedAnnotations} before diffing — otherwise every later form
   * PATCH would emit a spurious `remove …/annotations` op.
   *
   * @param existingDocumentId The integer document id whose annotations to persist.
   * @param annotations        The current placed-annotation set to write.
   * @returns Observable of the server's patched filing, or EMPTY when the
   *   filing/document cannot be resolved.
   */
  patchDocumentAnnotations(
    existingDocumentId: number,
    annotations: JudgeAnnotation[],
  ): Observable<JudgeReviewFiling> {
    const filing = this._filing();
    const filingId = filing?.reviewedFilingUniqueId;
    if (!filing || !filingId) {
      return EMPTY;
    }
    const idx = actionDocuments(filing).findIndex(
      d => d.existingDocumentId === existingDocumentId,
    );
    if (idx === -1) {
      return EMPTY;
    }
    const ops: JsonPatchOperation[] = [
      {
        op: 'replace',
        path: `${actionDocumentPath(idx)}/annotations`,
        value: this.mapAnnotationsToDto(annotations),
      },
    ];
    return this.apiService
      .patchJudgeReviewFiling(filingId, ops)
      .pipe(tap(result => this.setJudgeReviewFiling(result)));
  }

  /**
   * Returns the persisted annotations for a document as {@link JudgeAnnotation}[],
   * mapped from the filing DTO. Called during re-hydration on component init and
   * on active-document switch.
   *
   * @param existingDocumentId The integer document id to read annotations for.
   * @returns The persisted annotation objects, or an empty array when none exist.
   */
  getPersistedAnnotations(
    existingDocumentId: number | null,
  ): JudgeAnnotation[] {
    if (existingDocumentId == null) {
      return [];
    }
    const doc = actionDocuments(this._filing()).find(
      d => d.existingDocumentId === existingDocumentId,
    );
    return mapPersistedAnnotations(doc?.annotations);
  }

  /**
   * Maps the FE {@link JudgeAnnotation}[] to the API shape; see
   * {@link mapAnnotationsToDto}. Used by {@link patchDocumentAnnotations}
   * (debounced autosave) and by the studio component's KeepOriginal finalize
   * path, which embeds the live annotation set into the filing DTO before
   * diffing, without importing `@data-contracts` in the component.
   *
   * @param annotations The current in-memory annotation set to convert.
   * @returns The API-ready annotation array.
   */
  mapAnnotationsToDto(annotations: JudgeAnnotation[]): JudgeReviewAnnotation[] {
    return mapAnnotationsToDto(annotations);
  }

  clear(): void {
    this._filing.set(undefined);
    this._originalFiling.set(undefined);
    this._config.set(undefined);
    this._submitResponse.set(undefined);
  }
}
